<script setup lang="ts">
import { computed, ref, type Ref } from 'vue';
import HouseStageCircle from './HouseStageCircle.vue';
import moment from 'moment';

const props = defineProps<{
  houseModel: string;
  movingDate?: Date;
  loadingDays: boolean;
  loadingPhases: boolean;
  currentPhase?: number;
}>();

const currentStep: Ref<number> = computed(() => {
  if (props.currentPhase) {
    return props.currentPhase;
  }

  return 1;
});
const stepsContainer = ref();
const progressContainer = ref();

// eslint-disable-next-line complexity
const progressLineWidth = computed(() => {
  const initWidth = (progressContainer.value?.offsetWidth - stepsContainer.value?.offsetWidth) / 2;

  if (currentStep.value === 1) {
    return initWidth;
  }
  if (currentStep.value === 2) {
    let el = document.getElementById('stage-2-circle');
    if (!el) {
      return;
    }
    return el.offsetLeft;
  }
  if (currentStep.value === 3) {
    let el = document.getElementById('stage-3-circle');
    if (!el) {
      return;
    }
    return el.offsetLeft;
  }
  return progressContainer.value?.offsetWidth;
});

const daysToMove = computed(() => {
  if (!props.movingDate) {
    return 0;
  }

  const days = moment(props.movingDate).diff(new Date(), 'days');

  if (days < 0) {
    return 0;
  }

  return days;
});
</script>

<template>
  <div class="flex flex-col items-start gap-2 w-full">
    <Skeleton
      height="40px"
      width="147px"
      class="mb-2"
      border-radius="500px"
      style="margin-bottom: 0"
      v-if="loadingDays"
      :pt="{
        root: {
          style: {
            background: 'color-mix(in oklab, var(--color-black) 60%, transparent)'
          }
        }
      }"
    ></Skeleton>
    <div class="px-4 py-2 rounded-full bg-black/60 min-[1300px]:hidden" v-if="movingDate">
      <span class="text-white text-sm">
        <strong>{{ daysToMove }}</strong> {{ $t('stageInfo.daysToMove') }}
      </span>
    </div>
    <Skeleton
      height="180px"
      class="mb-2"
      border-radius="0.5rem"
      style="margin-bottom: 0"
      v-if="loadingPhases && !currentPhase"
      :pt="{
        root: {
          style: {
            background: 'color-mix(in oklab, var(--color-black) 60%, transparent)'
          }
        }
      }"
    ></Skeleton>
    <div
      class="flex flex-col gap-3 w-full min-[1300px]:gap-1 p-4 bg-black/60 rounded-lg min-[1300px]:p-6"
      v-if="!loadingPhases && currentPhase"
    >
      <div class="flex items-center justify-between w-full">
        <span
          class="text-[32px] min-[1300px]:text-[76px] font-medium min-[1300px]:font-medium leading-9 min-[1300px]:leading-[1.2] text-white"
        >
          {{ houseModel ? houseModel : '&nbsp;' }}
        </span>
      </div>
      <div
        ref="progressContainer"
        class="flex items-center justify-center relative w-full min-[1300px]:py-8"
      >
        <div class="absolute left-0 w-full h-0.5 bg-[#B4BEBD33]" />
        <div
          class="absolute left-0 h-0.5 bg-green-400"
          :style="{ width: progressLineWidth + 'px' }"
        />
        <div
          ref="stepsContainer"
          class="w-[246px] max-w-[246px] flex items-center justify-between z-10"
        >
          <HouseStageCircle stage="1" :is-active="currentStep === 1" />
          <HouseStageCircle stage="2" :is-active="currentStep === 2" id="stage-2-circle" />
          <HouseStageCircle stage="3" :is-active="currentStep === 3" id="stage-3-circle" />
          <HouseStageCircle stage="4" :is-active="currentStep === 4" />
        </div>
      </div>
      <div class="flex justify-between items-end text-white" v-if="!loadingPhases && currentPhase">
        <div class="flex flex-col gap-1">
          <span class="text-xs min-[1300px]:text-[20px]"
            >{{ $t('stageInfo.phase') }} 0{{ currentStep }}</span
          >
          <span class="text-sm min-[1300px]:text-[24px] font-bold">{{
            $t('stageInfo.phase.' + currentPhase)
          }}</span>
        </div>
        <div class="min-[1300px]:flex hidden" v-if="daysToMove > 0">
          <span class="text-sm min-[1300px]:text-[24px]" v-if="movingDate">
            <strong>{{ daysToMove }}</strong> {{ $t('stageInfo.daysToMove') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
